import { useUserSession } from '@solinkcorp/authentication';
import settings from '@solinkcorp/settings';
import useSWR from 'swr';

/** types from cloud-ai https://github.com/SolinkCorp/serverless-cloud-ai/pull/45 */
export type RequestMetrics =
  | 'prompt_tokens'
  | 'completion_tokens'
  | 'total_tokens'
  | 'images'
  | 'requests';

export interface UsageQuota {
  expiresAt: number;
  usage: {
    [requestMetric in RequestMetrics]: { count: number; remaining?: number };
  };
  retrievedIn: number;
  exhausted: boolean;
}

function useSpotCheckUsage(shouldFetch = false): UsageQuota | undefined {
  const userSession = useUserSession();

  const { data: usageStats } = useSWR(
    shouldFetch ? 'spot-check-usage-stats' : null,
    () => spotCheckUsageFetcher(userSession.accessToken),
    {
      revalidateOnFocus: false,
      errorRetryCount: 3,
    },
  );

  return usageStats;
}

export default useSpotCheckUsage;

async function spotCheckUsageFetcher(jwt: string): Promise<UsageQuota | undefined> {
  try {
    const response = await fetch(`${settings.cloudAIHost}/usageQuota/openAI/monthly`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwt}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch Spot Check usage stats`);
    }

    return await response.json();
  } catch (error) {
    throw new Error(`Failed to fetch Spot Check usage ${error}`);
  }
}
